import { useState, useEffect, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import Upload from './pages/Upload';
import TemplateReview from './pages/TemplateReview';
import Login from './pages/Login';
import Register from './pages/Register';
import Landing from './pages/Landing';
import Privacy from './pages/Privacy';
import AccountSettings from './pages/AccountSettings';
import FormatManager from './pages/FormatManager';
import ProtectedRoute from './components/ProtectedRoute';
import OperationPortal from './pages/OperationPortal';
import GovernanceWorkspace from './pages/GovernanceWorkspace';
import LoadingSpinner from './components/LoadingSpinner';
import PricingPage from './pages/PricingPage';
import { authApi } from './services/api';

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
    return useContext(AuthContext);
}

function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => {
        const stored = localStorage.getItem('token');
        return (stored && stored !== 'undefined' && stored !== 'null') ? stored : null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            fetchUser();
        } else {
            setLoading(false);
        }
    }, [token]);

    const fetchUser = async () => {
        try {
            const data = await authApi.me();
            setUser(data);
        } catch (err) {
            console.error('Auth fetch error:', err);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const login = async (username, password) => {
        const data = await authApi.login(username, password);

        const newToken = data.session?.access_token;
        if (newToken) {
            localStorage.setItem('token', newToken);
            setToken(newToken);
        }

        setUser(data.user);
        return data;
    };

    const register = async (email, username, password, orgName) => {
        const data = await authApi.register(email, username, password, orgName);

        const newToken = data.session?.access_token;
        if (newToken) {
            localStorage.setItem('token', newToken);
            setToken(newToken);
        }

        setUser(data.user);
        return data;
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const value = {
        user,
        token,
        loading,
        login,
        register,
        logout,
        isAuthenticated: !!user
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

// PrivateRoute is now replaced by ProtectedRoute.jsx for unified role management.
// We keep it as a simple alias temporarily if needed, but ProtectedRoute is preferred.

import FirstRun from './pages/FirstRun';
import OnboardingGatekeeper from './components/OnboardingGatekeeper';

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/landing" element={<Landing />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/privacy" element={<Privacy />} />

                    {/* Root Route - Landing for unauthenticated, Dashboard for authenticated */}
                    <Route path="/" element={<RootRoute />} />

                    {/* Onboarding Flow (Protected but Special) */}
                    <Route path="/onboarding" element={
                        <ProtectedRoute>
                            <FirstRun />
                        </ProtectedRoute>
                    } />
                    <Route path="/onboarding/create" element={
                        <ProtectedRoute roles={['admin']}>
                            <Upload isFirstRun={true} />
                        </ProtectedRoute>
                    } />

                    {/* Authenticated App Routes */}
                    <Route path="/app" element={
                        <ProtectedRoute>
                            <OnboardingGatekeeper>
                                <Layout />
                            </OnboardingGatekeeper>
                        </ProtectedRoute>
                    }>
                        <Route index element={<Dashboard />} />
                        <Route path="portal" element={<OperationPortal />} />
                        <Route path="governance" element={
                            <ProtectedRoute roles={['admin']}>
                                <GovernanceWorkspace />
                            </ProtectedRoute>
                        } />
                        <Route path="formatting" element={
                            <ProtectedRoute roles={['admin']}>
                                <FormatManager />
                            </ProtectedRoute>
                        } />
                        <Route path="upload" element={
                            <ProtectedRoute roles={['admin']}>
                                <Upload />
                            </ProtectedRoute>
                        } />
                        <Route path="templates/:id/review" element={
                            <ProtectedRoute roles={['admin']}>
                                <TemplateReview />
                            </ProtectedRoute>
                        } />
                        <Route path="pricing" element={<PricingPage />} />
                        <Route path="settings" element={<AccountSettings />} />
                    </Route>
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}

// Component to handle root route logic
function RootRoute() {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return <LoadingSpinner message="Initializing Spreadsheetr..." />;
    }

    if (isAuthenticated) {
        return <Navigate to="/app" replace />;
    }

    return <Landing />;
}

export default App;
